import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Checkbox,
  IconButton,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  Avatar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { InventoryItem, TableColumn } from '../types/inventory.types';

interface InventoryMobileCardProps {
  item: InventoryItem;
  visibleColumns: TableColumn[];
  bulkActionMode: boolean;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: (itemId: string) => void;
  onSelect: (itemId: string) => void;
  onEdit: (item: InventoryItem) => void;
  onDelete: (item: InventoryItem) => void;
  onViewDetails: (item: InventoryItem) => void;
  onAction: (item: InventoryItem, action: string) => void;
  onStockMovement?: (item: InventoryItem) => void; // ✅ YENİ: Stok Hareketi
  onStockMovementsReport?: (item: InventoryItem) => void; // ✅ YENİ: Hareket Raporu
  onPriceClick?: (item: InventoryItem) => void; // ✅ YENİ: Fiyat güncelleme için
  level?: number; // Hierarchical seviye (0 = ana ürün, 1 = varyant)
}

export const InventoryMobileCard: React.FC<InventoryMobileCardProps> = ({
  item,
  visibleColumns,
  bulkActionMode,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelect,
  onEdit,
  onDelete,
  onViewDetails,
  onAction,
  onStockMovement,
  onStockMovementsReport,
  onPriceClick,
  level = 0,
}) => {
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const handleActionClick = (action: string) => {
    onAction(item, action);
    handleCloseActionMenu();
  };

  // Sütun görünürlüğünü kontrol et
  const isColumnVisible = (field: string) => {
    return visibleColumns.some(col => col.field === field && col.visible);
  };

  // Resim URL'sini al
  const getImageUrl = () => {
    if (item.imageUrl) {
      // String ise direkt döndür
      if (typeof item.imageUrl === 'string') {
        return item.imageUrl;
      }
      // Array ise ilk elemanı döndür
      if (Array.isArray(item.imageUrl) && (item.imageUrl as string[]).length > 0) {
        return (item.imageUrl as string[])[0];
      }
    }
    return null;
  };

  // Para formatı
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // KALICI KURAL: parent/child kararinda parentItemId + isVariantParent otoritedir.
  // isMainItem sadece legacy uyum alani oldugu icin burada esas alinmaz.
  const isParentItem = item.isVariantParent === true || !item.parentItemId;
  const imageUrl = getImageUrl();

  // Kart rengi (varyant için açık gri)
  const cardBgColor = level > 0 ? 'grey.50' : 'white';
  const cardBorderColor = isSelected ? 'primary.main' : level > 0 ? 'grey.300' : 'divider';

  // Temel bilgileri grupla (item'da mevcut olmayan alanları gösterme)
  const basicInfo: { label: string; value: any; icon?: React.ReactNode }[] = [];
  
  // categoryName ve brandName gibi alanlar item'da yoksa gösterme
  if (isColumnVisible('barcode') && item.barcode) {
    basicInfo.push({ label: 'Barkod', value: item.barcode });
  }
  if (isColumnVisible('stockCode') && item.stockCode) {
    basicInfo.push({ label: 'Stok Kodu', value: item.stockCode });
  }

  // Fiyat bilgileri
  const priceInfo: { label: string; value: any }[] = [];
  
  if (isColumnVisible('salePrice')) {
    priceInfo.push({ label: 'Satış Fiyatı', value: formatCurrency(item.salePrice || 0) });
  }
  if (isColumnVisible('purchasePrice')) {
    priceInfo.push({ label: 'Alış Fiyatı', value: formatCurrency(item.purchasePrice || 0) });
  }
  if (isColumnVisible('vatRate')) {
    priceInfo.push({ label: 'KDV', value: `%${item.vatRate || 0}` });
  }
  if (isColumnVisible('departmentNo') && item.departmentNo) {
    priceInfo.push({ label: 'ÖKC', value: item.departmentNo });
  }

  // Stok bilgileri (warehouseStocks'tan hesaplanacak)
  const stockInfo: { label: string; value: any }[] = [];
  
  const totalStock = item.warehouseStocks?.reduce((sum, ws) => sum + (ws.quantity || 0), 0) || 0;
  
  if (isColumnVisible('quantity')) {
    stockInfo.push({ label: 'Mevcut Stok', value: `${totalStock} ${item.unit || 'Adet'}` });
  }
  if (isColumnVisible('minStockLevel') && item.minStockLevel) {
    stockInfo.push({ label: 'Min Stok', value: item.minStockLevel });
  }
  if (isColumnVisible('maxStockLevel') && item.maxStockLevel) {
    stockInfo.push({ label: 'Max Stok', value: item.maxStockLevel });
  }

  // E-Ticaret durumu
  const ecommerceInfo: { label: string; value: any; icon: React.ReactNode }[] = [];
  
  if (isColumnVisible('isEcommerce')) {
    ecommerceInfo.push({
      label: 'E-Ticaret',
      value: item.isEcommerce ? 'Açık' : 'Kapalı',
      icon: item.isEcommerce ? <CheckCircleIcon fontSize="small" color="success" /> : <CancelIcon fontSize="small" color="error" />
    });
  }
  if (isColumnVisible('isQRMenu')) {
    ecommerceInfo.push({
      label: 'QR Menü',
      value: item.isQRMenu ? 'Açık' : 'Kapalı',
      icon: item.isQRMenu ? <CheckCircleIcon fontSize="small" color="success" /> : <CancelIcon fontSize="small" color="error" />
    });
  }
  if (isColumnVisible('isKiosk')) {
    ecommerceInfo.push({
      label: 'Kiosk',
      value: item.isKiosk ? 'Açık' : 'Kapalı',
      icon: item.isKiosk ? <CheckCircleIcon fontSize="small" color="success" /> : <CancelIcon fontSize="small" color="error" />
    });
  }

  // Kart için özet bilgi (collapsed durumda gösterilecek)
  const summaryInfo: { label: string; value: any }[] = [];
  if (isColumnVisible('barcode') && item.barcode) {
    summaryInfo.push({ label: 'Barkod', value: item.barcode });
  }
  if (isColumnVisible('quantity')) {
    summaryInfo.push({ label: 'Stok', value: `${totalStock} ${item.unit || 'Adet'}` });
  }
  if (isColumnVisible('salePrice')) {
    summaryInfo.push({ label: 'Fiyat', value: formatCurrency(item.salePrice || 0) });
  }

  return (
    <Card
      elevation={isSelected ? 4 : 2}
      sx={{
        mb: 1.5,
        ml: level * 2, // Varyantlar için indent
        borderRadius: 2,
        border: '2px solid',
        borderColor: cardBorderColor,
        bgcolor: cardBgColor,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: 4,
          borderColor: 'primary.main',
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Header: Checkbox + Ürün Adı + 3 Nokta + Expand/Collapse */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" flex={1} gap={1}>
            {/* Checkbox (sadece toplu işlem modunda) */}
            {bulkActionMode && (
              <Checkbox
                checked={isSelected}
                onChange={() => onSelect(item.id)}
                size="small"
                sx={{ p: 0 }}
              />
            )}

            {/* Hierarchical gösterge (varyant için) */}
            {level > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                └─
              </Typography>
            )}

            {/* Ürün Adı */}
            <Box flex={1} minWidth={0}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  fontSize: level > 0 ? '0.85rem' : '0.95rem',
                }}
              >
                {item.name}
              </Typography>
              {/* Ana ürün için varyant sayısı gösterme - bu bilgi item'da yok */}
            </Box>
          </Box>

          {/* 3 Nokta Menü Butonu */}
          <IconButton
            size="small"
            onClick={handleOpenActionMenu}
            sx={{ p: 0.5, mr: 0.5 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>

          {/* Expand/Collapse butonu */}
          <IconButton
            size="small"
            onClick={() => onToggleExpand(item.id)}
            sx={{ p: 0.5 }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Ana İçerik: Resim + Özet Bilgiler (Collapsed) */}
        {!isExpanded && (
          <Box display="flex" gap={1.5} mb={1}>
            {/* Resim */}
            {imageUrl ? (
              <Avatar
                src={imageUrl}
                variant="rounded"
                sx={{ width: 75, height: 75, border: '1px solid', borderColor: 'divider' }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ width: 75, height: 75, bgcolor: 'grey.200', border: '1px solid', borderColor: 'divider' }}
              >
                <PhotoCameraIcon sx={{ fontSize: 32, color: 'grey.400' }} />
              </Avatar>
            )}

            {/* Özet Bilgiler */}
            <Box flex={1} display="flex" flexDirection="column" gap={0.3}>
              {summaryInfo.slice(0, 4).map((info, index) => (
                <Typography key={index} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                  <strong>{info.label}:</strong> {info.value}
                </Typography>
              ))}
            </Box>
          </Box>
        )}

        {/* Detaylı İçerik (Expanded) */}
        <Collapse in={isExpanded}>
          <Box>
            {/* Resim + Temel Bilgiler */}
            <Box display="flex" gap={1.5} mb={1.5}>
              {/* Resim */}
              {imageUrl ? (
                <Avatar
                  src={imageUrl}
                  variant="rounded"
                  sx={{ width: 75, height: 75, border: '1px solid', borderColor: 'divider' }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{ width: 75, height: 75, bgcolor: 'grey.200', border: '1px solid', borderColor: 'divider' }}
                >
                  <PhotoCameraIcon sx={{ fontSize: 32, color: 'grey.400' }} />
                </Avatar>
              )}

              {/* Temel Bilgiler */}
              {basicInfo.length > 0 && (
                <Box flex={1}>
                  <Typography variant="caption" fontWeight="bold" color="primary.main" sx={{ mb: 0.5, display: 'block' }}>
                    📊 Temel Bilgiler
                  </Typography>
                  {basicInfo.map((info, index) => (
                    <Typography key={index} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                      • {info.label}: <strong>{info.value}</strong>
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            {/* Fiyat Bilgileri - Tıklanabilir */}
            {priceInfo.length > 0 && (
              <Box 
                mb={1.5}
                onClick={() => onPriceClick?.(item)}
                sx={{
                  cursor: onPriceClick ? 'pointer' : 'default',
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'success.50',
                  border: '1px solid',
                  borderColor: 'success.200',
                  transition: 'all 0.2s',
                  '&:hover': onPriceClick ? {
                    bgcolor: 'success.100',
                    borderColor: 'success.main',
                    transform: 'scale(1.01)',
                  } : {},
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                  💰 Fiyat Bilgileri {onPriceClick && '(Güncellemek için tıkla)'}
                </Typography>
                {priceInfo.map((info, index) => (
                  <Typography key={index} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                    • {info.label}: <strong>{info.value}</strong>
                  </Typography>
                ))}
              </Box>
            )}

            {/* Stok Bilgileri */}
            {stockInfo.length > 0 && (
              <Box mb={1.5}>
                <Typography variant="caption" fontWeight="bold" color="info.main" sx={{ mb: 0.5, display: 'block' }}>
                  📦 Stok Bilgileri
                </Typography>
                {stockInfo.map((info, index) => (
                  <Typography key={index} variant="caption" color="text.secondary" sx={{ lineHeight: 1.4, display: 'block' }}>
                    • {info.label}: <strong>{info.value}</strong>
                  </Typography>
                ))}
              </Box>
            )}

            {/* E-Ticaret Durumu */}
            {ecommerceInfo.length > 0 && (
              <Box mb={1.5}>
                <Typography variant="caption" fontWeight="bold" color="secondary.main" sx={{ mb: 0.5, display: 'block' }}>
                  🌐 E-Ticaret Durumu
                </Typography>
                {ecommerceInfo.map((info, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={0.5} mb={0.3}>
                    {info.icon}
                    <Typography variant="caption" color="text.secondary">
                      • {info.label}: <strong>{info.value}</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Aksiyon Menüsü */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {/* ✅ YENİ: Stok Ayrıntıları */}
          <MenuItem onClick={() => { onViewDetails(item); handleCloseActionMenu(); }}>
            <InfoIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} /> Stok Ayrıntıları
          </MenuItem>
          
          {/* ✅ YENİ: Stok Hareketi */}
          {onStockMovement && (
            <MenuItem onClick={() => { onStockMovement(item); handleCloseActionMenu(); }}>
              <InventoryIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} /> Stok Hareketi
            </MenuItem>
          )}
          
          {/* ✅ YENİ: Hareket Raporu */}
          {onStockMovementsReport && (
            <MenuItem onClick={() => { onStockMovementsReport(item); handleCloseActionMenu(); }}>
              <AssessmentIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> Hareket Raporu
            </MenuItem>
          )}
          
          {/* ✅ YENİ: Düzenle */}
          <MenuItem onClick={() => { onEdit(item); handleCloseActionMenu(); }}>
            <EditIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> Düzenle
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={() => handleActionClick('imageLibrary')}>
            <PhotoCameraIcon fontSize="small" sx={{ mr: 1 }} /> Resim Ekle
          </MenuItem>
          <MenuItem onClick={() => handleActionClick('toggleEcommerce')}>
            <StorefrontIcon fontSize="small" sx={{ mr: 1 }} /> E-Ticaret {item.isEcommerce ? 'Kapat' : 'Aç'}
          </MenuItem>
          <MenuItem onClick={() => handleActionClick('toggleQRMenu')}>
            <QrCodeIcon fontSize="small" sx={{ mr: 1 }} /> QR Menü {item.isQRMenu ? 'Kapat' : 'Aç'}
          </MenuItem>
          <MenuItem onClick={() => handleActionClick('toggleKiosk')}>
            <RestaurantIcon fontSize="small" sx={{ mr: 1 }} /> Kiosk {item.isKiosk ? 'Kapat' : 'Aç'}
          </MenuItem>
          <MenuItem onClick={() => handleActionClick('sendToN11')}>
            <CloudUploadIcon fontSize="small" sx={{ mr: 1 }} /> N11'e Gönder
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={() => { onDelete(item); handleCloseActionMenu(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Sil
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

